import type { StaffPrivacyRequestsQuery } from "@lafayette/schemas";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { type PrivacyPolling, usePrivacyRuntime } from "./runtime";

// The reads of the privacy screens. An answer holds ids of customers, so nothing stays in the cache after the page lets go (`gcTime: 0`) and
// nothing is written to browser storage. A failed read is not retried by the library: the page shows the error and offers a retry.
//
// A request that is APPROVED or EXECUTING changes without a click (a worker runs it), so the list is read again every few seconds while one
// is open. The polling stops after two minutes (a deletion that is still open then is slow or stuck: the "Refresh" button starts it again).

export const privacyKeys = {
  all: ["staff-privacy"] as const,
  requests: (query: StaffPrivacyRequestsQuery) => ["staff-privacy", "requests", query] as const,
  matrix: ["staff-privacy", "matrix"] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

const PRIVACY_POLL_MS = 5_000;
const PRIVACY_POLL_LIMIT_MS = 120_000;

const isRunning = (state: string) => state === "APPROVED" || state === "EXECUTING";

/**
 * `refreshToken` changes when staff press "Refresh" or finish an action: the two-minute window of the polling starts again.
 */
export function usePrivacyRequests(
  query: StaffPrivacyRequestsQuery,
  enabled: boolean,
  refreshToken = 0,
) {
  const { api, polling: configured } = usePrivacyRuntime();
  const polling: PrivacyPolling = configured ?? {
    everyMs: PRIVACY_POLL_MS,
    limitMs: PRIVACY_POLL_LIMIT_MS,
  };
  const pollingSince = useRef<number | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the token is the signal, the effect only resets the window.
  useEffect(() => {
    pollingSince.current = null;
  }, [refreshToken]);
  return useQuery({
    queryKey: privacyKeys.requests(query),
    queryFn: ({ signal }) => api.listRequests(query, { signal }),
    enabled,
    ...READ_OPTIONS,
    refetchInterval: (current) => {
      const open = current.state.data?.items.some((item) => isRunning(item.state)) ?? false;
      if (!open) {
        pollingSince.current = null;
        return false;
      }
      pollingSince.current ??= Date.now();
      return Date.now() - pollingSince.current < polling.limitMs ? polling.everyMs : false;
    },
  });
}

export function usePrivacyMatrix(enabled: boolean) {
  const { api } = usePrivacyRuntime();
  return useQuery({
    queryKey: privacyKeys.matrix,
    queryFn: ({ signal }) => api.matrix({ signal }),
    enabled,
    ...READ_OPTIONS,
  });
}
